/**
 * PÁGINA DE LOGIN
 * 
 * Login con skins por app.
 * Soporta Email/Password, Teléfono/Contraseña y Teléfono/OTP.
 * NO valida permisos, solo autentica.
 */

import React, { useState, FormEvent, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getBrandingConfig } from '@shared/config/brandingConfig';
import { HoneypotField } from '@shared/components/HoneypotField';
import {
  signInWithEmailPassword,
  signInWithPhonePassword,
  sendHybridLoginCodes,
  verifyPhoneOTP,
  buildAccountsSsoRedirectUrl,
} from '@shared/auth';
import { useDynamicBranding } from '../hooks/useDynamicBranding';
import { usePhoneLogin } from '@shared/hooks/usePhoneLogin';
import { supabase } from '@shared/lib/supabaseClient';
import { Button } from '@shared/ui/shadcn/ui/button';
import { Alert, AlertDescription } from '@shared/ui/shadcn/ui/alert';
import { Input } from '@shared/ui/shadcn/ui/input';
import { Checkbox } from '@shared/ui/shadcn/ui/checkbox';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import AuthLayout from '../components/AuthLayout';
import { LoginTabs, LoginTabsList, LoginTabsTrigger, LoginTabsContent } from '../components/LoginTabs';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { useAuthIntent } from '../contexts/AuthIntentContext';
import { createSsoTrace } from '@shared/utils/ssoTrace';
import {
  getAppSSOConfig,
  getDefaultReturnTo,
  parseTrustedLocalOriginForApp,
  resolveHandoffAppBaseUrl,
  shouldUseDevOrigins,
  APP_SSO_CONFIGS,
} from '@shared/navigation/appSSOConfig';
import { formatPhoneDisplay, normalizeToE164, detectInputType } from '@shared/utils/phoneUtils';
import { cn } from '@shared/lib/utils';
import { LibretaIdentityVerification } from '../components/LibretaIdentityVerification';
import { afterLaLibretaAuthSession } from '../services/libretaIdentityCheck';
import type { LibretaAvailableFactors } from '../services/libretaIdentityCheck';

/**
 * 🔧 Helper: Obtener URL del Core API según el entorno
 * - DEV: localhost:4000 (Core API local)
 * - PROD: api.elfom.com (producción - SSO centralizado)
 */
const getCoreApiUrl = (): string => {
  // ✅ LOGGING TEMPORAL PARA DIAGNÓSTICO
  // ✅ Limpiar URL de espacios y saltos de línea (problema común en variables de entorno)
  const envUrl = (import.meta.env.VITE_CORE_API_URL || '').trim();
  const isDev = import.meta.env.DEV;
  const isProd = import.meta.env.PROD;
  
  console.log('🔍 [getCoreApiUrl] Debug:', {
    'VITE_CORE_API_URL': envUrl || 'UNDEFINED',
    'DEV': isDev,
    'PROD': isProd,
    'typeof VITE_CORE_API_URL': typeof envUrl,
  });
  
  // Si está definida en variables de entorno, usarla
  if (envUrl) {
    console.log('✅ [getCoreApiUrl] Usando VITE_CORE_API_URL:', envUrl);
    return envUrl;
  }
  
  // Fallback según entorno
  const fallback = isDev 
    ? 'http://localhost:4000' 
    : 'https://api.elfom.com';
  console.log('⚠️ [getCoreApiUrl] VITE_CORE_API_URL no definida, usando fallback:', fallback);
  return fallback;
};

/**
 * 🔐 SSO PRODUCCIÓN: Establecer cookie httpOnly después de login exitoso
 * Solo funciona en PRODUCCIÓN (cookies cross-domain)
 */
export const setSSOCookie = async (accessToken: string): Promise<void> => {
  // Solo en producción
  if (!import.meta.env.PROD) {
    console.log('[SSO] DEV mode: Cookie no creada (SSO solo en PROD)');
    return;
  }

  try {
    const coreApiUrl = getCoreApiUrl();
    const response = await fetch(`${coreApiUrl}/auth/set-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Incluir cookies
      body: JSON.stringify({
        access_token: accessToken,
      }),
    });

    if (response.ok) {
      console.log('✅ [SSO] Cookie de sesión creada en producción');
    } else {
      const errorText = await response.text();
      console.warn('⚠️ [SSO] Error creando cookie de sesión:', errorText);
    }
  } catch (err) {
    console.error('❌ [SSO] Error llamando a /auth/set-session:', err);
    // No bloquear el flujo si falla la cookie
  }
};

/**
 * ✅ Helper: Normalizar returnTo de forma segura
 * Convierte URLs absolutas a rutas relativas si el origen coincide
 * 
 * @param appOrigin - Origen esperado de la app destino (ej: "http://localhost:5173")
 * @param returnToRaw - returnTo crudo (puede ser null, ruta relativa, o URL absoluta)
 * @param trace - Trace logger para observabilidad
 * @returns Ruta relativa normalizada o null si se rechaza
 */
/**
 * Normaliza returnTo: convierte URLs absolutas a rutas relativas
 * @param returnToRaw - returnTo crudo (puede ser null, ruta relativa, o URL absoluta)
 * @param _app - Nombre de la app (reservado para uso futuro)
 * @returns Ruta relativa normalizada o null si se rechaza
 */
const normalizeReturnTo = (returnToRaw: string | null, _app: string): string | null => {
  if (!returnToRaw) return null;

  // Si viene URL absoluta, convertir a ruta (pathname+search)
  try {
    if (returnToRaw.startsWith('http://') || returnToRaw.startsWith('https://')) {
      const u = new URL(returnToRaw);
      return (u.pathname || '/') + (u.search || '');
    }
  } catch {}

  // Si ya es relativa segura
  if (returnToRaw.startsWith('/') && !returnToRaw.startsWith('//')) return returnToRaw;

  return null;
};

/**
 * ✅ Usar mapping centralizado - SOLO houseme y thefomlabs
 * DEFAULT_RETURN_TO ahora viene de appSSOConfig.ts
 */

/**
 * 🔄 HANDOFF SSO: Crear handoff y redirigir a app destino
 * 
 * Flujo:
 * 1. Obtener sesión completa de Supabase
 * 2. Llamar POST /auth/handoff con sesión
 * 3. Redirigir a app destino con código
 * 
 * @param app - Nombre de la app destino
 * @param returnTo - Ruta opcional en la app destino (ej: "/dashboard")
 * @param onError - Callback para manejar errores (muestra mensaje al usuario)
 * @param trace - Trace logger para observabilidad
 * @returns true si fue exitoso, false si hubo error
 */
export const handleHandoffRedirect = async (
  app: string,
  returnTo: string | null,
  onError: (message: string) => void,
  trace: ReturnType<typeof createSsoTrace>,
  /** Origen de la app cliente (ej. http://localhost:5183) si login en Accounts prod. */
  clientOriginHint?: string | null,
): Promise<boolean> => {
  try {
    // 1. Obtener sesión completa de Supabase
    trace.step('SESSION_GET_START');
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !session) {
      trace.error('SESSION_GET_ERROR', { error: sessionError?.message });
      console.error('[ACCOUNTS] handoff falló: sin sesión', sessionError?.message);
      onError('Error: No se pudo obtener la sesión. Por favor intenta de nuevo.');
      return false;
    }

    if (!session.access_token) {
      trace.error('SESSION_INVALID', { reason: 'missing_access_token' });
      console.error('[ACCOUNTS] handoff falló: sesión sin access_token');
      onError('Error: Sesión inválida. Por favor intenta de nuevo.');
      return false;
    }

    trace.step('SESSION_GET_OK', {
      hasAccessToken: !!session.access_token,
      hasRefreshToken: !!session.refresh_token,
      accessTokenMasked: trace.maskToken(session.access_token),
      userId: session.user?.id,
      userEmail: session.user?.email,
    });

    // 2. Validar y normalizar app y returnTo
    // ✅ TODAS las apps deben estar en APP_SSO_CONFIGS
    let normalizedApp = app?.trim() || null;
    
    // ✅ CRÍTICO: Si no hay app o no está soportada, mostrar error y NO redirigir
    if (!normalizedApp || !APP_SSO_CONFIGS[normalizedApp]) {
      const supportedApps = Object.keys(APP_SSO_CONFIGS).join(', ');
      trace.error('APP_NOT_SUPPORTED', { app: normalizedApp || 'null', supportedApps });
      console.error('[ACCOUNTS] handoff falló: app no soportada', normalizedApp, supportedApps);
      onError(`Error: La aplicación "${normalizedApp || 'desconocida'}" no está soportada. Apps soportadas: ${supportedApps}`);
      return false;
    }
    
    // ✅ Normalizar returnTo: convertir URLs absolutas a rutas relativas
    const normalizedReturnTo = normalizeReturnTo(returnTo, normalizedApp);
    
    // 🔍 DEBUG: Log de normalización
    trace.step('RETURN_TO_NORMALIZATION', {
      returnToRaw: returnTo,
      normalizedReturnTo: normalizedReturnTo,
      app: normalizedApp,
    });
    
    // ✅ Si después de normalizar queda vacío o '/', usar defaultRoute de ESA app
    const defaultRoute = getDefaultReturnTo(normalizedApp);
    const safeReturnTo = (normalizedReturnTo && normalizedReturnTo !== '/') 
      ? normalizedReturnTo 
      : (defaultRoute || '/dashboard'); // Fallback solo si defaultRoute es null (no debería pasar)
    
    // 🔍 DEBUG: Log de safeReturnTo
    trace.step('SAFE_RETURN_TO_RESOLVED', {
      normalizedReturnTo: normalizedReturnTo,
      defaultRoute: defaultRoute,
      safeReturnTo: safeReturnTo,
      usedDefault: safeReturnTo === defaultRoute,
    });
    
    const appBaseUrl = resolveHandoffAppBaseUrl(normalizedApp, clientOriginHint);

    if (!appBaseUrl) {
      trace.error('APP_ORIGIN_NOT_FOUND', { app: normalizedApp });
      console.error('[ACCOUNTS] handoff falló: appBaseUrl null', normalizedApp);
      onError(`Error: No se pudo determinar la URL de destino para la aplicación "${normalizedApp}"`);
      return false;
    }

    trace.step('HANDOFF_TARGET_ORIGIN', {
      app: normalizedApp,
      appBaseUrl,
      usedClientOriginHint: Boolean(
        clientOriginHint?.trim() && parseTrustedLocalOriginForApp(normalizedApp, clientOriginHint),
      ),
    });

    // 3. Llamar al endpoint /auth/handoff
    const coreApiUrl = getCoreApiUrl();
    
    // ✅ LOGGING TEMPORAL
    console.log('🚀 [handleHandoffRedirect] URL del Core API:', coreApiUrl);
    console.log('🚀 [handleHandoffRedirect] URL completa:', `${coreApiUrl}/auth/handoff`);
    
    const handoffBody: any = {
      app: normalizedApp,
    };

    // Solo incluir session si refresh_token está disponible (recomendado)
    // Si no está disponible, el endpoint usará fallback con solo access_token
    if (session.refresh_token) {
      handoffBody.session = {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      };
    }

    // ✅ Usar safeReturnTo (siempre relativo, con fallback a default) en el body del handoff
    if (safeReturnTo && safeReturnTo.startsWith('/')) {
      handoffBody.returnTo = safeReturnTo;
    }

    const handoffUrl = `${coreApiUrl}/auth/handoff`;
    trace.step('HANDOFF_POST_START', {
      url: handoffUrl,
      app: normalizedApp,
      returnTo: safeReturnTo,
      normalizedReturnTo,
      hasRefreshToken: !!session.refresh_token,
      headers: ['Authorization', 'Content-Type', 'X-Trace-Id'],
      traceId: trace.traceId,
    });

    // ✅ Log adicional para debugging
    console.log('🚀 [ACCOUNTS] Ejecutando fetch a /auth/handoff', {
      url: handoffUrl,
      traceId: trace.traceId,
      hasAccessToken: !!session.access_token,
      bodyKeys: Object.keys(handoffBody),
      timestamp: new Date().toISOString(),
    });

    const HANDOFF_TIMEOUT_MS = 15000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), HANDOFF_TIMEOUT_MS);
    let handoffResponse: Response;
    try {
      handoffResponse = await fetch(handoffUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'X-Trace-Id': trace.traceId, // ✅ Correlación con Core API
        },
        body: JSON.stringify(handoffBody),
        signal: controller.signal,
      });
    } catch (fetchErr: unknown) {
      clearTimeout(timeoutId);
      const isAbort = fetchErr instanceof Error && fetchErr.name === 'AbortError';
      trace.error('HANDOFF_FETCH_ERROR', { error: (fetchErr as Error)?.message, isAbort });
      console.error('[ACCOUNTS] handoff falló: fetch error', fetchErr);
      onError(isAbort ? 'El servidor tardó demasiado en responder. Intenta de nuevo.' : 'Error de conexión. Intenta de nuevo.');
      return false;
    }
    clearTimeout(timeoutId);
    
    // ✅ Log después del fetch
    console.log('📥 [ACCOUNTS] Respuesta de /auth/handoff recibida', {
      status: handoffResponse.status,
      ok: handoffResponse.ok,
      traceId: trace.traceId,
      timestamp: new Date().toISOString(),
    });

    trace.step('HANDOFF_POST_RESPONSE', {
      status: handoffResponse.status,
      ok: handoffResponse.ok,
      statusText: handoffResponse.statusText,
    });

    if (!handoffResponse.ok) {
      const errorData = await handoffResponse.json().catch(() => ({ error: 'Error desconocido' }));
      trace.error('HANDOFF_POST_ERROR_BODY', {
        status: handoffResponse.status,
        error: errorData.error || 'Unknown error',
        message: errorData.message || errorData.error || 'Error desconocido',
      });
      console.error('[ACCOUNTS] handoff falló: respuesta no OK', handoffResponse.status, errorData);
      const errorMessage = errorData.message || errorData.error || 'Error al crear código de acceso';
      onError(`Error: ${errorMessage}. Por favor intenta de nuevo.`);
      return false;
    }

    const handoffData = await handoffResponse.json();
    const { code, returnTo: handoffReturnTo } = handoffData;

    if (!code) {
      trace.error('HANDOFF_RESPONSE_NO_CODE', { response: handoffData });
      console.error('[ACCOUNTS] handoff falló: sin code en respuesta', handoffData);
      onError('Error: No se recibió código de acceso. Por favor intenta de nuevo.');
      return false;
    }

    // 4. Construir URL de redirección
    let redirectUrl: string;
    
    // ✅ CRÍTICO: Detectar si returnTo es un deep link (houseme://, empresa://, etc.)
    // Si es deep link, redirigir directamente al deep link con el código
    // Si no, usar el flujo web normal con /auth/callback
    const isDeepLink = returnTo && returnTo.includes('://') && !returnTo.startsWith('http');
    
    if (isDeepLink) {
      // Es un deep link (ej: houseme://auth/callback)
      // Redirigir directamente al deep link con el código
      const separator = returnTo!.includes('?') ? '&' : '?';
      redirectUrl = `${returnTo}${separator}code=${encodeURIComponent(code)}&traceId=${encodeURIComponent(trace.traceId)}`;
      
      trace.step('REDIRECT_BUILD_DEEPLINK', {
        deepLink: returnTo,
        codeMasked: trace.maskCode(code),
        traceId: trace.traceId,
        isDeepLink: true,
      });
    } else {
      // Es una URL web normal, usar el flujo con /auth/callback
      // ✅ Construir URL de redirección usando safeReturnTo (con fallback a default por app)
      // SIEMPRE usar /auth/callback como ruta base, y returnTo como query param
      const returnToPath = safeReturnTo;

      // ✅ Construir URL final: appBaseUrl + /auth/callback + ?code=...&returnTo=...&traceId=...
      redirectUrl = `${appBaseUrl}/auth/callback?code=${encodeURIComponent(code)}&returnTo=${encodeURIComponent(returnToPath)}&traceId=${encodeURIComponent(trace.traceId)}`;

      trace.step('REDIRECT_BUILD_WEB', {
        targetOrigin: appBaseUrl,
        callbackPath: '/auth/callback',
        returnToPath: returnToPath,
        safeReturnTo: safeReturnTo,
        normalizedReturnTo: normalizedReturnTo,
        codeMasked: trace.maskCode(code),
        traceId: trace.traceId,
        isDeepLink: false,
      });
    }

    trace.step('REDIRECT_EXECUTE', {
      finalUrl: redirectUrl.replace(code, trace.maskCode(code)),
      isDeepLink,
    });

    // 5. Redirigir
    const safeUrl = redirectUrl.replace(code, '***CODE***');
    console.log('[ACCOUNTS] 🚀 Redirigiendo a:', { url: safeUrl, isDeepLink });
    console.log('%c[ACCOUNTS] REDIRECT OK → ' + safeUrl, 'color: green; font-weight: bold;');
    window.location.href = redirectUrl;
    return true;
  } catch (err: any) {
    trace.error('HANDOFF_UNEXPECTED_ERROR', {
      error: err.message,
      stack: err.stack?.substring(0, 200),
    });
    onError('Error inesperado. Por favor intenta de nuevo.');
    return false;
  }
};

const LoginPage: React.FC = () => {
  const [params] = useSearchParams();
  const { setAuthIntent } = useAuthIntent();
  
  // ✅ Inferir app desde parámetros o contexto
  let app = params.get('app');
  const returnTo = params.get('returnTo');
  const intent = params.get('intent');
  const source = params.get('source');
  /** Origen de la app que abrió el login (p. ej. http://localhost:5183) vía redirectToLogin. */
  const clientOriginParam = params.get('clientOrigin');
  
  // ✅ Si app es 'default' o no viene, inferir desde contexto
  if (!app || app === 'default') {
    // ✅ PRIORIDAD 1: Si viene source, usar ese como app
    if (source === 'thefomlabs') {
      app = 'thefomlabs'; // ✅ CORRECTO - regresar a thefomlabs
    }
    else if (source === 'empresa') {
      app = 'empresa';
    }
    else if (source === 'houseme') {
      app = 'houseme';
    }
    else if (source === 'tienda') {
      app = 'tienda';
    }
    else if (source === 'gobscore') {
      app = 'gobscore';
    }
    else if (source === 'la-libreta') {
      app = 'la-libreta';
    }
    // ✅ PRIORIDAD 2: Si returnTo contiene la app, inferir desde ahí
    else if (returnTo && returnTo.includes('/empresa')) {
      app = 'empresa';
    }
    else if (returnTo && returnTo.includes('/thefomlabs')) {
      app = 'thefomlabs';
    }
    else if (returnTo && returnTo.includes('/houseme')) {
      app = 'houseme';
    }
    else if (returnTo && returnTo.includes('/tienda')) {
      app = 'tienda';
    }
    else if (returnTo && (returnTo.includes('/map') || returnTo.includes('/report') || returnTo.includes('/alerts'))) {
      app = 'gobscore';
    }
    else if (returnTo && (returnTo.includes('lalibreta') || returnTo.includes('localhost:5184'))) {
      app = 'la-libreta';
    }
    // Si no se puede inferir, dejar como null para que se muestre error
    else {
      app = null;
    }
  }

  // #region agent log
  // Removed debug fetch to 127.0.0.1:7242 - service not available
  // #endregion

  // Leer intent y source al montar y persistirlos en AuthIntentContext
  // Usar useRef para evitar ejecuciones múltiples
  const intentSetRef = useRef(false);
  useEffect(() => {
    // #region agent log
    // Removed debug fetch to 127.0.0.1:7242 - service not available
    // #endregion
    // Protección: solo ejecutar una vez
    if (intentSetRef.current) {
      return;
    }
    intentSetRef.current = true;
    
    if (intent || source || returnTo) {
      setAuthIntent({
        intent: intent || null,
        source: source || null,
        returnTo: returnTo || null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo al montar - NO incluir setAuthIntent ni otros params para evitar loops

  // ✅ Aplicar branding dinámico basado en el parámetro app
  // Si app es desconocido o null, usa branding por defecto de "Accounts Central"
  useDynamicBranding(app);
  
  // ✅ Validar app temprano - si es null, mostrar error antes de renderizar
  const [appError, setAppError] = useState<string | null>(null);
  
  useEffect(() => {
    if (!app || !APP_SSO_CONFIGS[app]) {
      const supportedApps = Object.keys(APP_SSO_CONFIGS).join(', ');
      setAppError(`Error: La aplicación "${app || 'desconocida'}" no está soportada. Apps soportadas: ${supportedApps}`);
    } else {
      setAppError(null);
    }
  }, [app]);
  
  // ✅ Obtener configuración de branding (fallback a 'default' si app es desconocido)
  const branding = getBrandingConfig(app);

  // Estado para método de login (por defecto: teléfono + contraseña)
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone-password' | 'email-otp'>('phone-password');
  
  // Estado para email/password
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Estado para teléfono + contraseña
  const [phonePassword, setPhonePassword] = useState('');
  const [passwordForPhone, setPasswordForPhone] = useState('');
  const [showPasswordPhone, setShowPasswordPhone] = useState(false);
  const [rememberMePhone, setRememberMePhone] = useState(false);
  
  // Estados de foco para placeholders condicionales
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [phoneOtpFocused, setPhoneOtpFocused] = useState(false);

  // Hook para login por teléfono/OTP
  const { state: phoneState, sendOTP, verifyOTP, reset: resetPhoneLogin } = usePhoneLogin();
  const [phoneInput, setPhoneInput] = useState('');
  const [otpInput, setOtpInput] = useState('');

  // Flujo híbrido: un solo campo (email o teléfono); se detecta el tipo y se envía a ambos canales (correo + móvil)
  const [hybridEmailOrPhone, setHybridEmailOrPhone] = useState('');
  const [hybridResolvedEmail, setHybridResolvedEmail] = useState('');
  const [hybridResolvedPhone, setHybridResolvedPhone] = useState('');
  const [hybridCodesSent, setHybridCodesSent] = useState(false);
  const [hybridExistingUserDetected, setHybridExistingUserDetected] = useState(false);
  const [hybridResendCooldown, setHybridResendCooldown] = useState(0);
  const [hybridEmailOrPhoneFocused, setHybridEmailOrPhoneFocused] = useState(false);
  const hybridHandoffDoneRef = useRef(false);
  const hybridOtpAutoSubmitRef = useRef(false);

  /** Segundo factor La Libreta (cédula / correo) tras OTP o login con sesión. */
  const LIBRETA_2FA_MAX_ATTEMPTS = 3;
  const [libreta2FA, setLibreta2FA] = useState<{
    userId: string;
    available_factors: LibretaAvailableFactors;
  } | null>(null);
  const [libretaVerifyFailCount, setLibretaVerifyFailCount] = useState(0);
  const libretaHandoffContextRef = useRef<{
    returnTo: string | null;
    trace: ReturnType<typeof createSsoTrace>;
    clientOrigin: string | null;
  } | null>(null);

  useEffect(() => {
    if (app !== 'la-libreta') {
      setLibreta2FA(null);
      setLibretaVerifyFailCount(0);
      libretaHandoffContextRef.current = null;
    }
  }, [app]);

  // Cargar credenciales guardadas al montar
  useEffect(() => {
    try {
      const savedEmail = localStorage.getItem('saved_email');
      const savedPassword = localStorage.getItem('saved_password');
      const savedPhone = localStorage.getItem('saved_phone');
      const savedPhonePassword = localStorage.getItem('saved_phone_password');

      if (savedEmail && savedPassword) {
        setEmail(savedEmail);
        setPassword(savedPassword);
        setRememberMe(true);
      }

      if (savedPhone && savedPhonePassword) {
        setPhonePassword(savedPhone);
        setPasswordForPhone(savedPhonePassword);
        setRememberMePhone(true);
      }
    } catch (error) {
      console.error('Error cargando credenciales guardadas:', error);
    }
  }, []);

  // Teléfono opcional desde la landing / enlaces externos (?phone=1809… o E.164)
  const phoneFromQuery = params.get('phone');
  useEffect(() => {
    if (!phoneFromQuery?.trim()) return;
    const e164 = normalizeToE164(phoneFromQuery);
    const display = e164
      ? formatPhoneDisplay(e164)
      : formatPhoneDisplay(phoneFromQuery.replace(/\D/g, ''));
    if (display) setPhonePassword(display);
  }, [phoneFromQuery]);

  // ✅ Reiniciar loading al montar/volver a la página para que el botón no quede congelado
  useEffect(() => {
    setLoading(false);
  }, []);

  // Fase 3: Auto-submit al completar 6 dígitos (flujo híbrido OTP)
  useEffect(() => {
    if (otpInput.length < 6) {
      hybridOtpAutoSubmitRef.current = false;
      return;
    }
    if (
      otpInput.length === 6 &&
      hybridCodesSent &&
      hybridResolvedPhone &&
      app &&
      APP_SSO_CONFIGS[app] &&
      !loading &&
      !hybridOtpAutoSubmitRef.current
    ) {
      hybridOtpAutoSubmitRef.current = true;
      handleVerifyHybridOTP({ preventDefault: () => {} } as FormEvent);
    }
  }, [otpInput, hybridCodesSent, hybridResolvedPhone, app, loading]);

  // Rate limiting helper (best-effort, no bloquea si falla)
  const rlInvoke = async (body: any) => {
    try {
      const { data, error } = await supabase.functions.invoke("login-rate-limit", { body });
      if (error) {
        console.warn("[Login] login-rate-limit error:", error);
        return null;
      }
      return data ?? null;
    } catch (e) {
      console.warn("[Login] login-rate-limit exception:", e);
      return null;
    }
  };

  /** Handoff SSO o pantalla 2FA La Libreta según Edge `libreta-identity-check`. */
  const runHandoffWithLaLibretaGate = async (
    session: { user: { id: string } },
    returnToParam: string | null,
    trace: ReturnType<typeof createSsoTrace>,
    clientOriginHint: string | null | undefined
  ): Promise<'redirected' | 'second_factor' | 'error'> => {
    if (!app || !APP_SSO_CONFIGS[app]) {
      setError('App no soportada');
      return 'error';
    }

    const gate = await afterLaLibretaAuthSession(app, session.user.id);
    if (gate.next === 'error') {
      setError(gate.message);
      return 'error';
    }
    if (gate.next === 'second_factor') {
      libretaHandoffContextRef.current = {
        returnTo: returnToParam,
        trace,
        clientOrigin: clientOriginHint ?? null,
      };
      setLibretaVerifyFailCount(0);
      setLibreta2FA({
        userId: session.user.id,
        available_factors: gate.available_factors,
      });
      return 'second_factor';
    }

    const ok = await handleHandoffRedirect(
      app,
      returnToParam,
      (msg) => setError(msg),
      trace,
      clientOriginHint ?? undefined
    );
    return ok ? 'redirected' : 'error';
  };

  // Handler para email/password
  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const fd = new FormData(form);
    if (fd.get('website')) return; // honeypot
    setLoading(true);
    setError(null);

    // ✅ Crear trace único para este intento de login
    const params = new URLSearchParams(window.location.search);
    const returnToParam = params.get('returnTo');
    const trace = createSsoTrace({ app: 'accounts', feature: 'SSO_HANDOFF' });
    
    trace.step('LOGIN_START', {
      method: 'email',
      app: app || 'null',
      returnTo: returnToParam,
    });
    
    // ✅ Validar app antes de continuar
    if (!app || !APP_SSO_CONFIGS[app]) {
      const supportedApps = Object.keys(APP_SSO_CONFIGS).join(', ');
      trace.error('APP_NOT_SUPPORTED', { 
        app: app || 'null',
        supportedApps,
      });
      setError(`Error: La aplicación "${app || 'desconocida'}" no está soportada. Apps soportadas: ${supportedApps}`);
      setLoading(false);
      return;
    }

    try {
      // Check rate limit (best-effort)
      const rlCheck = await rlInvoke({ email, ip_address: null, action: "check" });
      if (rlCheck?.blocked === true) {
        setError("Demasiados intentos. Intenta nuevamente en 15 minutos.");
        setLoading(false);
        return;
      }

      const { error: signInError } = await signInWithEmailPassword(email, password);

      // Record attempt (fire-and-forget)
      if (signInError) {
        void rlInvoke({ email, ip_address: null, action: "record", successful: false });
        trace.error('LOGIN_ERROR', { error: signInError.message });
        setError(signInError.message);
        setLoading(false);
        return;
      }

      void rlInvoke({ email, ip_address: null, action: "record", successful: true });

      // Guardar o limpiar credenciales según el checkbox
      try {
        if (rememberMe) {
          localStorage.setItem('saved_email', email);
          localStorage.setItem('saved_password', password);
        } else {
          localStorage.removeItem('saved_email');
          localStorage.removeItem('saved_password');
        }
      } catch (error) {
        console.error('Error guardando credenciales:', error);
      }

      // Verificar sesión después del login
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (!session) {
        trace.error('SESSION_NOT_FOUND', { error: sessionError?.message });
        setError('Error: No se pudo obtener la sesión. Por favor intenta de nuevo.');
        setLoading(false);
        return;
      }

      // ✅ CENTRALIZADO: Notificar al Core API del login exitoso (DEV y PROD)
      if (session.access_token) {
        const { notifyCoreAPILogin } = await import('@shared/services/coreAuthService');
        await notifyCoreAPILogin(session.access_token);
        
        // ✅ SSO PRODUCCIÓN: También crear cookie httpOnly si estamos en producción (compatibilidad)
        await setSSOCookie(session.access_token);
      }

      // ✅ HANDOFF SSO (La Libreta: puede mostrar 2FA antes)
      const handoffOutcome = await runHandoffWithLaLibretaGate(session, returnToParam, trace, clientOriginParam);
      if (handoffOutcome === 'second_factor') {
        setLoading(false);
        return;
      }
      if (handoffOutcome === 'error') {
        setLoading(false);
        return;
      }
    } catch (err: any) {
      trace.error('LOGIN_UNEXPECTED_ERROR', { error: err.message });
      setError('Error inesperado. Por favor intenta de nuevo.');
      setLoading(false);
    }
  };

  // Handler para teléfono + contraseña
  const handlePhonePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const fd = new FormData(form);
    if (fd.get('website')) return; // honeypot
    setLoading(true);
    setError(null);

    // ✅ Crear trace único para este intento de login
    const params = new URLSearchParams(window.location.search);
    const returnToParam = params.get('returnTo');
    const trace = createSsoTrace({ app: 'accounts', feature: 'SSO_HANDOFF' });
    
    trace.step('LOGIN_START', {
      method: 'phone-password',
      app: app || 'null',
      returnTo: returnToParam,
    });
    
    // ✅ Validar app antes de continuar
    if (!app || !APP_SSO_CONFIGS[app]) {
      const supportedApps = Object.keys(APP_SSO_CONFIGS).join(', ');
      trace.error('APP_NOT_SUPPORTED', { 
        app: app || 'null',
        supportedApps,
      });
      setError(`Error: La aplicación "${app || 'desconocida'}" no está soportada. Apps soportadas: ${supportedApps}`);
      setLoading(false);
      return;
    }

    try {
      const { error: signInError } = await signInWithPhonePassword(phonePassword, passwordForPhone);

      if (signInError) {
        trace.error('LOGIN_ERROR', { error: signInError.message });
        setError(signInError.message);
        setLoading(false);
        return;
      }

      // Guardar o limpiar credenciales según el checkbox
      try {
        if (rememberMePhone) {
          localStorage.setItem('saved_phone', phonePassword);
          localStorage.setItem('saved_phone_password', passwordForPhone);
        } else {
          localStorage.removeItem('saved_phone');
          localStorage.removeItem('saved_phone_password');
        }
      } catch (error) {
        console.error('Error guardando credenciales:', error);
      }

      // Verificar sesión después del login
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (!session) {
        trace.error('SESSION_NOT_FOUND', { error: sessionError?.message });
        setError('Error: No se pudo obtener la sesión. Por favor intenta de nuevo.');
        setLoading(false);
        return;
      }

      // ✅ CENTRALIZADO: Notificar al Core API del login exitoso (DEV y PROD)
      if (session.access_token) {
        const { notifyCoreAPILogin } = await import('@shared/services/coreAuthService');
        await notifyCoreAPILogin(session.access_token);
        
        // ✅ SSO PRODUCCIÓN: También crear cookie httpOnly si estamos en producción (compatibilidad)
        await setSSOCookie(session.access_token);
      }

      const phoneHandoffOutcome = await runHandoffWithLaLibretaGate(session, returnToParam, trace, clientOriginParam);
      if (phoneHandoffOutcome === 'second_factor') {
        setLoading(false);
        return;
      }
      if (phoneHandoffOutcome === 'error') {
        setLoading(false);
        return;
      }
    } catch (err: any) {
      trace.error('LOGIN_UNEXPECTED_ERROR', { error: err.message });
      setError('Error inesperado. Por favor intenta de nuevo.');
      setLoading(false);
    }
  };

  // Handler para enviar OTP
  const handleSendOTP = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const success = await sendOTP(phoneInput);
    if (!success && !phoneState.error) {
      setError('Error al enviar código OTP');
    }
  };

  // Handler híbrido: un solo campo (email o teléfono). Se detecta el tipo, se resuelve el otro y se envía a ambos canales (correo + móvil).
  const handleSendHybridCodes = async (e: FormEvent) => {
    e?.preventDefault?.();
    const input = hybridEmailOrPhone.trim();
    if (!input) {
      setError('Por favor ingresa tu correo o tu número de teléfono');
      return;
    }

    const inputType = detectInputType(input);
    if (!inputType) {
      setError('Ingresa un correo válido o un número de teléfono');
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);
    setHybridExistingUserDetected(false);

    const params = new URLSearchParams(window.location.search);
    const returnToParam = params.get('returnTo');
    const trace = createSsoTrace({ app: 'accounts', feature: 'HYBRID_LOGIN' });

    if (!app || !APP_SSO_CONFIGS[app]) {
      setError(`App "${app || 'desconocida'}" no soportada.`);
      setLoading(false);
      return;
    }

    let resolvedEmail = '';
    let resolvedPhone = '';

    try {
      if (inputType === 'email') {
        const normalizedEmail = input.toLowerCase();
        const { data: emailCheckData, error: emailCheckError } = await supabase.rpc('verificar_email_existe', {
          p_email: normalizedEmail,
        });
        const emailExists = !emailCheckError && emailCheckData?.length > 0 && emailCheckData[0]?.email_exists === true;
        if (!emailExists) {
          setError('No hay una cuenta con este correo. Crea una cuenta primero o usa otro método de acceso.');
          setLoading(false);
          trace.step('HYBRID_USER_NOT_FOUND', {});
          return;
        }
        resolvedEmail = normalizedEmail;
        const { data: phoneData, error: phoneError } = await supabase.rpc('obtener_telefono_por_email', {
          p_email: normalizedEmail,
        });
        if (phoneError || !phoneData?.length || !phoneData[0]?.phone) {
          setError('No tenemos un teléfono registrado para este correo. Registra tu teléfono en tu perfil para usar "Sin contraseña", o usa Email/Teléfono con contraseña.');
          setLoading(false);
          return;
        }
        const rawPhone = String(phoneData[0].phone).trim();
        resolvedPhone = normalizeToE164(rawPhone) || rawPhone;
      } else {
        const phoneDigits = input.replace(/\D/g, '');
        const telefonoParam = phoneDigits || input;
        console.log('[Login] 🔍 Buscando email por teléfono (RPC):', { telefonoParam, supabaseUrl: import.meta.env.VITE_SUPABASE_URL });
        const { data: emailData, error: emailError } = await supabase.rpc('obtener_email_por_telefono', {
          p_telefono: telefonoParam,
        });
        if (emailError || !emailData?.length || !emailData[0]?.email) {
          console.log('[Login] ❌ obtener_email_por_telefono falló:', {
            emailError: emailError ? { message: emailError.message, code: emailError.code, details: emailError.details } : null,
            emailDataLength: emailData?.length,
            firstRow: emailData?.[0],
          });
          setError('No hay una cuenta con este teléfono. Crea una cuenta primero o usa otro método de acceso.');
          setLoading(false);
          trace.step('HYBRID_USER_NOT_FOUND', {});
          return;
        }
        resolvedEmail = String(emailData[0].email).trim().toLowerCase();
        const rawPhone = normalizeToE164(input) || phoneDigits || input;
        resolvedPhone = normalizeToE164(rawPhone) || rawPhone;
      }

      setHybridExistingUserDetected(true);
      setHybridResolvedEmail(resolvedEmail);
      setHybridResolvedPhone(resolvedPhone);
      setHybridCodesSent(true);
      setHybridResendCooldown(30);
      setMessage('Revisa tu correo (enlace para entrar) y tu WhatsApp (código de 6 dígitos).');
      setLoading(false);

      const accountsUrl = import.meta.env.VITE_ACCOUNTS_URL || window.location.origin;
      const redirectUrl = buildAccountsSsoRedirectUrl('/auth/callback', {
        baseOrigin: accountsUrl,
        returnTo: returnToParam ?? undefined,
        app: app || undefined,
        clientOrigin: clientOriginParam ?? undefined,
      });

      // Enviar en segundo plano: no bloquear la UI; el usuario pasa de inmediato al campo de código
      sendHybridLoginCodes(resolvedEmail, resolvedPhone, redirectUrl, app || undefined)
        .then((result) => {
          trace.step('HYBRID_SENT', { successEmail: result.successEmail, successPhone: result.successPhone });
          if (result.error) setError(result.error);
        })
        .catch((err: any) => {
          trace.error('HYBRID_SEND_ERROR', { error: err?.message });
          setError(err?.message || 'Error al enviar. Usa "Reenviar código" para intentar de nuevo.');
        });
    } catch (err: any) {
      trace.error('HYBRID_SEND_ERROR', { error: err.message });
      setError(err?.message || 'Error al enviar códigos');
    } finally {
      setLoading(false);
    }
  };

  // Listener: si el usuario confirma por enlace de email (otra pestaña), completar SSO aquí
  useEffect(() => {
    if (!hybridCodesSent || !app || !APP_SSO_CONFIGS[app] || hybridHandoffDoneRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const returnToParam = params.get('returnTo');

    const unsubscribe = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event !== 'SIGNED_IN' || !session?.access_token || !session.user?.id || hybridHandoffDoneRef.current) return;
      hybridHandoffDoneRef.current = true;
      const trace = createSsoTrace({ app: 'accounts', feature: 'HYBRID_EMAIL_CONFIRMED' });
      try {
        const { notifyCoreAPILogin } = await import('@shared/services/coreAuthService');
        await notifyCoreAPILogin(session.access_token);
        await setSSOCookie(session.access_token);
      } catch (_) {}
      const co = new URLSearchParams(window.location.search).get('clientOrigin');
      const emailLinkOutcome = await runHandoffWithLaLibretaGate(session, returnToParam, trace, co);
      if (emailLinkOutcome === 'error') {
        hybridHandoffDoneRef.current = false;
      }
    });

    return () => {
      unsubscribe.data.subscription.unsubscribe();
    };
  }, [hybridCodesSent, app]);

  // Cuenta atrás 30 s para reenviar código OTP (Login híbrido)
  useEffect(() => {
    if (!hybridCodesSent || hybridResendCooldown <= 0) return;
    const t = setInterval(() => setHybridResendCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(t);
  }, [hybridCodesSent, hybridResendCooldown]);

  const handleResendHybridCodes = async () => {
    if (hybridResendCooldown > 0 || loading) return;
    if (!hybridResolvedEmail || !hybridResolvedPhone || !app || !APP_SSO_CONFIGS[app]) return;
    const params = new URLSearchParams(window.location.search);
    const returnToParam = params.get('returnTo');
    const accountsUrl = import.meta.env.VITE_ACCOUNTS_URL || window.location.origin;
    const redirectUrl = buildAccountsSsoRedirectUrl('/auth/callback', {
      baseOrigin: accountsUrl,
      returnTo: returnToParam ?? undefined,
      app: app || undefined,
      clientOrigin: clientOriginParam ?? undefined,
    });
    setError(null);
    setOtpInput('');
    hybridOtpAutoSubmitRef.current = false;
    setLoading(true);
    try {
      const result = await sendHybridLoginCodes(hybridResolvedEmail, hybridResolvedPhone, redirectUrl, app || undefined);
      setError(result.error || null);
      setHybridResendCooldown(30);
      setMessage('Código reenviado. Revisa tu WhatsApp o correo.');
      setTimeout(() => setMessage(null), 5000);
    } catch (err: any) {
      setError(err?.message || 'Error al reenviar.');
    } finally {
      setLoading(false);
    }
  };

  // Red de seguridad: si el flujo completo no termina en 25s, salir de "Accediendo..."
  const SAFETY_TIMEOUT_MS = 25000;
  const handleVerifyHybridOTP = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!app || !APP_SSO_CONFIGS[app]) return;
    if (otpInput.length !== 6) {
      setError('El código debe tener 6 dígitos');
      return;
    }

    const returnToParam = params.get('returnTo');
    const trace = createSsoTrace({ app: 'accounts', feature: 'SSO_HANDOFF' });
    trace.step('LOGIN_START', { method: 'hybrid-otp', app, returnTo: returnToParam });

    // Bloquear onAuthStateChange desde YA: solo este flujo hará el handoff (evitar race con SIGNED_IN)
    hybridHandoffDoneRef.current = true;

    // Log en Accounts: parámetros de la URL para depuración
    console.log('[ACCOUNTS] hybrid OTP flow', { app, returnTo: returnToParam, fromUrl: window.location.href });

    setLoading(true);
    let safetyCleared = false;
    const safetyId = setTimeout(() => {
      if (safetyCleared) return;
      safetyCleared = true;
      hybridOtpAutoSubmitRef.current = true; // Bloquear auto-reintentos
      console.log('[Login] hybrid OTP: safety timeout → forzando setLoading(false)');
      setError('La verificación tardó demasiado. Intenta de nuevo.');
      setLoading(false);
    }, SAFETY_TIMEOUT_MS);

    try {
      console.log('[Login] hybrid OTP: verifyPhoneOTP start');
      const result = await verifyPhoneOTP(hybridResolvedPhone, otpInput, hybridResolvedEmail || undefined);
      if (safetyCleared) return; // ya mostramos error por timeout, no sobrescribir
      console.log('[Login] hybrid OTP: verifyPhoneOTP done', result?.success);
      if (!result.success) {
        hybridOtpAutoSubmitRef.current = true; // Bloquear auto-reintentos (evitar bucle infinito)
        setError(result.error || 'Código inválido');
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.error('[Login] hybrid OTP: no session after verify');
        setError('No se pudo obtener la sesión. Intenta de nuevo.');
        return;
      }
      // Solo actualizar phone en cliente si la Edge no lo hizo (cuando no se pasó email)
      if (session?.user?.id && !hybridResolvedEmail) {
        const phoneE164 = normalizeToE164(hybridResolvedPhone) || hybridResolvedPhone;
        if (phoneE164) {
          supabase.from('app_users').update({ phone: phoneE164 }).eq('user_id', session.user.id).then(() => {}, () => {});
        }
      }
      // Notificar Core API en background (no bloquear): si Core API no responde,
      // el handoff debe completarse igual; la cookie/notificación es secundaria
      if (session?.access_token) {
        const { notifyCoreAPILogin } = await import('@shared/services/coreAuthService');
        Promise.race([
          Promise.all([
            notifyCoreAPILogin(session.access_token),
            setSSOCookie(session.access_token),
          ]),
          new Promise<void>((r) => setTimeout(r, 5000)),
        ]).catch(() => {});
      }

      console.log('[Login] hybrid OTP: handoff start', { app, returnTo: returnToParam, hasSession: !!session?.access_token });
      const hybridOutcome = await runHandoffWithLaLibretaGate(session, returnToParam, trace, clientOriginParam);
      console.log('[Login] hybrid OTP: handoff done', { hybridOutcome });
      if (hybridOutcome === 'second_factor') {
        return;
      }
      if (hybridOutcome === 'error') {
        setError('No se pudo redirigir. Intenta de nuevo.');
      }
    } catch (err: unknown) {
      if (safetyCleared) return;
      hybridOtpAutoSubmitRef.current = true; // Bloquear auto-reintentos
      console.log('[Login] hybrid OTP: error', err instanceof Error ? err.message : err);
      const msg = err instanceof Error ? err.message : 'Error de conexión. Intenta de nuevo.';
      setError(msg);
    } finally {
      clearTimeout(safetyId);
      if (!safetyCleared) {
        console.log('[Login] hybrid OTP: finally → setLoading(false)');
        setLoading(false);
      }
    }
  };

  // Handler para verificar OTP
  const handleVerifyOTP = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // ✅ Crear trace único para este intento de login
    const params = new URLSearchParams(window.location.search);
    const returnToParam = params.get('returnTo');
    const trace = createSsoTrace({ app: 'accounts', feature: 'SSO_HANDOFF' });

    trace.step('LOGIN_START', {
      method: 'email-otp',
      app: app || 'null',
      returnTo: returnToParam,
    });

    // ✅ Validar app antes de continuar
    if (!app || !APP_SSO_CONFIGS[app]) {
      const supportedApps = Object.keys(APP_SSO_CONFIGS).join(', ');
      trace.error('APP_NOT_SUPPORTED', { app: app || 'null', supportedApps });
      setError(`Error: La aplicación "${app || 'desconocida'}" no está soportada. Apps soportadas: ${supportedApps}`);
      setLoading(false);
      return;
    }

    const success = await verifyOTP(otpInput);

    if (success) {
      const sessionResult = await supabase.auth.getSession();
      if (sessionResult.data.session) {
        if (sessionResult.data.session.access_token) {
          const { notifyCoreAPILogin } = await import('@shared/services/coreAuthService');
          Promise.race([
            Promise.all([
              notifyCoreAPILogin(sessionResult.data.session.access_token),
              setSSOCookie(sessionResult.data.session.access_token),
            ]),
            new Promise<void>((r) => setTimeout(r, 5000)),
          ]).catch(() => {});

          const otpTabOutcome = await runHandoffWithLaLibretaGate(
            sessionResult.data.session,
            returnToParam,
            trace,
            clientOriginParam
          );
          if (otpTabOutcome === 'second_factor') {
            setLoading(false);
            return;
          }
          if (otpTabOutcome === 'error') {
            setLoading(false);
            return;
          }
          // Redirección en curso, no resetear loading
        } else {
          trace.error('SESSION_NOT_CREATED', {});
          setError('Error: La sesión no se creó. Por favor intenta de nuevo.');
          setLoading(false);
        }
      } else {
        trace.error('SESSION_NOT_CREATED', {});
        setError('Error: La sesión no se creó. Por favor intenta de nuevo.');
        setLoading(false);
      }
    } else {
      trace.error('OTP_VERIFICATION_FAILED', {});
      setError('Código inválido o expirado. Solicita uno nuevo.');
      setLoading(false);
    }
  };

  const completeLibretaSecondFactor = async () => {
    const ctx = libretaHandoffContextRef.current;
    if (!ctx || !app) return;
    setError(null);
    setLoading(true);
    const ok = await handleHandoffRedirect(
      app,
      ctx.returnTo,
      (msg) => setError(msg),
      ctx.trace,
      ctx.clientOrigin ?? undefined
    );
    setLoading(false);
    if (ok) {
      setLibreta2FA(null);
      libretaHandoffContextRef.current = null;
      setLibretaVerifyFailCount(0);
    }
  };

  const registerLibreta2FAFailure = () => {
    setLibretaVerifyFailCount((c) => c + 1);
  };

  if (libreta2FA && app === 'la-libreta') {
    return (
      <AuthLayout>
        <div className="w-full max-w-md mx-auto">
          {error ? (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <LibretaIdentityVerification
            authUserId={libreta2FA.userId}
            availableFactors={libreta2FA.available_factors}
            onVerified={completeLibretaSecondFactor}
            onFailedAttempt={registerLibreta2FAFailure}
            failedAttempts={libretaVerifyFailCount}
            maxAttempts={LIBRETA_2FA_MAX_ATTEMPTS}
          />
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {/* ✅ LAYOUT LIMPIO - Identidad nativa de cada app */}
      <div className="w-full flex flex-col gap-6">
        {/* Logo + Título - Identidad visual de la app */}
        <div className="text-center space-y-3">
          {/* Logo con tratamiento especial para HouseMe y Empresa */}
          {app === 'houseme' ? (
            <div className="flex items-center justify-center gap-2">
              <img 
                src={branding.assets.logo || "/icon-houseme-192x192.png"} 
                alt="HouseMe" 
                className="h-8 w-8"
              />
              <span className="text-lg lg:text-xl font-bold">
                <span style={{ color: `hsl(${branding.colors.primary})` }}>House</span>
                <span style={{ color: `hsl(${branding.colors.secondary || branding.colors.primary})` }}>Me</span>
              </span>
            </div>
          ) : app === 'empresa' ? (
            <div className="flex items-center justify-center gap-2">
              <img 
                src={branding.assets.logo || "/icon-192x192.png"} 
                alt="ThePlatform360" 
                className="h-8 w-8"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (target.src !== window.location.origin + '/icon-192x192.png') {
                    target.src = '/icon-192x192.png';
                  }
                }}
              />
              <span className="text-lg lg:text-xl font-bold" style={{ color: `hsl(${branding.colors.primary})` }}>
                ThePlatform360
              </span>
            </div>
          ) : branding.assets.logo ? (
            <img 
              src={branding.assets.logo} 
              alt={branding.label} 
              className="h-12 mx-auto"
            />
          ) : null}
          
          <div>
            <h1 className="text-xl font-semibold text-foreground">
              {branding.copy.title}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {branding.copy.subtitle}
            </p>
          </div>
        </div>

        {/* Formulario principal - Sin tabs visibles */}
        <LoginTabs 
          value={loginMethod} 
          onValueChange={(value) => setLoginMethod(value as 'email' | 'phone-password' | 'email-otp')}
        >
          {/* Tabs ocultos - solo para la lógica interna */}
          <div className="hidden">
            <LoginTabsList>
              <LoginTabsTrigger value="email">Email</LoginTabsTrigger>
              <LoginTabsTrigger value="phone-password">Teléfono</LoginTabsTrigger>
              <LoginTabsTrigger value="email-otp">Email OTP</LoginTabsTrigger>
            </LoginTabsList>
          </div>

          {/* Tab: Email/Password */}
          <LoginTabsContent value="email" className="mt-0">
            {/* ✅ BOTÓN PRINCIPAL: Continuar con Google (SSO centralizado) */}
            <GoogleSignInButton 
              returnTo={returnTo || undefined}
              app={app || undefined}
              className="w-full mb-4"
              onSignInStart={() => setLoading(true)}
              onError={(error) => {
                setLoading(false);
                setError(error.message || 'Error al iniciar sesión con Google');
              }}
            />
            
            {/* Separador */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-border" />
              <span className="text-sm text-muted-foreground">o</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <HoneypotField />
              {(error || appError) && (
                <Alert variant="destructive">
                  <AlertDescription>{appError || error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-1.5">
                <Input
                  id="email"
                  type="email"
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setEmailFocused(true)}
                  onBlur={() => setEmailFocused(false)}
                  required
                  autoComplete="email"
                  placeholder={emailFocused ? "Escríbelo aquí" : ""}
                  className="placeholder:text-muted-foreground/40"
                />
              </div>

              <div className="space-y-1.5">
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    label="Contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    required
                    autoComplete="current-password"
                    placeholder={passwordFocused ? "Escríbela aquí" : ""}
                    className="pr-10 placeholder:text-muted-foreground/40"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Checkbox: Recordar contraseña */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="remember-email" 
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                />
                <label
                  htmlFor="remember-email"
                  className="text-sm text-muted-foreground cursor-pointer select-none"
                >
                  Recordar contraseña
                </label>
              </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full"
              size="lg"
            >
              {loading ? 'Iniciando sesión...' : branding.copy.buttonText || 'Iniciar sesión'}
            </Button>
          </form>
        </LoginTabsContent>

          {/* Tab: Teléfono + Contraseña */}
          <LoginTabsContent value="phone-password" className="mt-0">
            {/* ✅ BOTÓN PRINCIPAL: Continuar con Google (SSO centralizado) */}
            <GoogleSignInButton 
              returnTo={returnTo || undefined}
              app={app || undefined}
              className="w-full mb-4"
              onSignInStart={() => setLoading(true)}
              onError={(error) => {
                setLoading(false);
                setError(error.message || 'Error al iniciar sesión con Google');
              }}
            />
            
            {/* Separador */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-border" />
              <span className="text-sm text-muted-foreground">o</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            <form onSubmit={handlePhonePasswordSubmit} className="space-y-4">
              <HoneypotField />
              {(error || appError) && (
                <Alert variant="destructive">
                  <AlertDescription>{appError || error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-1.5">
                <Input
                  id="phone-password"
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  label="Número de teléfono"
                  value={phonePassword}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setPhonePassword(value);
                  }}
                  required
                  placeholder="Escríbelo aquí"
                  disabled={loading}
                  className="placeholder:text-muted-foreground/40"
                />
              </div>

              <div className="space-y-1.5">
                <div className="relative">
                  <Input
                    id="password-phone"
                    type={showPasswordPhone ? 'text' : 'password'}
                    label="Contraseña"
                    value={passwordForPhone}
                    onChange={(e) => setPasswordForPhone(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="Escríbela aquí"
                    disabled={loading}
                    className="pr-10 placeholder:text-muted-foreground/40"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordPhone(!showPasswordPhone)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                    disabled={loading}
                  >
                    {showPasswordPhone ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Checkbox: Recordar contraseña */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="remember-phone" 
                  checked={rememberMePhone}
                  onCheckedChange={(checked) => setRememberMePhone(checked as boolean)}
                />
                <label
                  htmlFor="remember-phone"
                  className="text-sm text-muted-foreground cursor-pointer select-none"
                >
                  Recordar contraseña
                </label>
              </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full"
              size="lg"
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
            </Button>
          </form>
        </LoginTabsContent>

          {/* Tab: Híbrido Email + OTP (enlace por correo y código por teléfono) */}
          <LoginTabsContent value="email-otp" className="mt-0">
            <div className="space-y-4">
              {(error || appError) && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {appError || error}
                    {error && String(error).includes('No hay una cuenta') && (
                      <>
                        {' '}
                        <Link
                          to={`/signup?app=${app}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`}
                          className="underline font-medium"
                        >
                          Regístrate aquí
                        </Link>
                      </>
                    )}
                  </AlertDescription>
                </Alert>
              )}
              {message && (
                <Alert variant="default" className="bg-green-50 border-green-200 text-green-800">
                  <AlertDescription>{message}</AlertDescription>
                </Alert>
              )}

              {!hybridCodesSent ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSendHybridCodes(e);
                  }}
                  className="space-y-4"
                >
                  <div className="space-y-1.5">
                    <Input
                      id="hybrid-email-or-phone"
                      type="text"
                      inputMode="text"
                      label="Email o teléfono"
                      value={hybridEmailOrPhone}
                      onChange={(e) => setHybridEmailOrPhone(e.target.value)}
                      onFocus={() => setHybridEmailOrPhoneFocused(true)}
                      onBlur={() => setHybridEmailOrPhoneFocused(false)}
                      placeholder={hybridEmailOrPhoneFocused ? 'Correo o número de teléfono' : ''}
                      disabled={loading}
                      className="placeholder:text-lg placeholder:text-muted-foreground/70"
                      autoComplete="username"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Te enviaremos un enlace al correo y un código por WhatsApp (según lo que tengas registrado).
                  </p>
                  <Button
                    type="submit"
                    disabled={!hybridEmailOrPhone.trim() || loading}
                    className="w-full"
                    size="lg"
                  >
                    {loading ? 'Enviando...' : 'Enviar código'}
                  </Button>
                </form>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    Ingresa el código de 6 dígitos que enviamos a tu WhatsApp (o revisa tu correo para el enlace).
                  </p>
                  <div className="flex flex-col gap-1 rounded-md bg-muted/50 px-3 py-2 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground">Enviado a</span>
                      <button
                        type="button"
                        onClick={() => { setHybridCodesSent(false); setOtpInput(''); setError(null); setMessage(null); setHybridResolvedEmail(''); setHybridResolvedPhone(''); }}
                        className="text-primary hover:underline font-medium text-xs shrink-0"
                      >
                        Editar
                      </button>
                    </div>
                    <span className="font-medium tabular-nums">
                      Correo: {hybridResolvedEmail || '—'} · Teléfono: {formatPhoneDisplay(normalizeToE164(hybridResolvedPhone) || hybridResolvedPhone) || hybridResolvedPhone || '—'}
                    </span>
                  </div>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (otpInput.length === 6) handleVerifyHybridOTP(e);
                    }}
                    className="space-y-4"
                  >
                    <div className="space-y-1.5">
                      <Input
                        id="hybrid-otp"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        label="Código de 6 dígitos"
                        value={otpInput}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setOtpInput(v);
                          if (error) setError(null);
                        }}
                        placeholder="000000"
                        disabled={loading}
                        className={cn(
                          'placeholder:text-muted-foreground/40',
                          error && '!border-destructive focus-visible:!ring-destructive'
                        )}
                      />
                    </div>
                    {otpInput.length === 6 && loading && (
                      <p className="text-center text-sm text-muted-foreground animate-pulse">
                        Validando código…
                      </p>
                    )}
                    <p className="text-center text-xs text-muted-foreground">
                      {hybridResendCooldown > 0 ? (
                        <>Reenviar código en <span className="font-medium tabular-nums">{hybridResendCooldown}</span> s</>
                      ) : (
                        <button
                          type="button"
                          onClick={handleResendHybridCodes}
                          disabled={loading}
                          className="text-primary hover:underline font-medium disabled:opacity-50"
                        >
                          Reenviar código
                        </button>
                      )}
                    </p>
                  </form>
                </>
              )}
            </div>
        </LoginTabsContent>
        </LoginTabs>

        {/* Footer compacto */}
        <div className="mt-4 text-center text-xs text-muted-foreground space-y-1.5">
          {/* Métodos alternativos + recuperar contraseña en una línea */}
          <div>
            {loginMethod !== 'email' && (
              <>
                <button
                  type="button"
                  onClick={() => setLoginMethod('email')}
                  className="text-primary hover:underline font-medium"
                >
                  Email
                </button>
                {' · '}
              </>
            )}
            {loginMethod !== 'phone-password' && (
              <>
                <button
                  type="button"
                  onClick={() => setLoginMethod('phone-password')}
                  className="text-primary hover:underline font-medium"
                >
                  Teléfono
                </button>
                {' · '}
              </>
            )}
            {loginMethod !== 'email-otp' && (
              <>
                <button
                  type="button"
                  onClick={() => setLoginMethod('email-otp')}
                  className="text-primary hover:underline font-medium"
                >
                  Sin contraseña
                </button>
              </>
            )}
            {(loginMethod === 'email' || loginMethod === 'phone-password') && (
              <>
                {' · '}
                <Link
                  to={`/forgot-password?app=${app}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`}
                  className="text-primary hover:underline font-medium"
                >
                  Recuperar contraseña
                </Link>
              </>
            )}
          </div>

          {/* Registro */}
          <div>
            ¿No tienes cuenta?{' '}
            <Link
              to={`/signup?app=${app}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`}
              className="text-primary hover:underline font-medium"
            >
              Regístrate
            </Link>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default LoginPage;
